// 🔧 research_queue.js
// ✅ Purpose: Admin-facing Research Queue for Let's Go Vote Sales Automation
// ✅ Features:
// - Shows prospects selected for research by the Collection Manager
// - Shows prospects missing research that have website/source URLs
// - Prioritizes by queue priority, fit score, and created date
// - Allows admins to run the Research Agent directly
// - Allows admins to skip research without deleting the prospect
// - Does NOT send emails or social messages
// - Uses top-of-page success/error/info notifications
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import SalesPageLayout from "../../components/sales/SalesPageLayout";

function ResearchQueue() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [researchingId, setResearchingId] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [runningResearchManager, setRunningResearchManager] = useState(false);

  const showStatus = (type, message) => {
    setStatus({ type, message });
    setTimeout(() => setStatus(null), 20000);
  };

  const loadProspects = async () => {
    try {
      setLoading(true);

      const prospectsSnap = await getDocs(
        query(collection(db, "prospects"), orderBy("createdAt", "desc"))
      );

      setProspects(
        prospectsSnap.docs.map((prospectDoc) => ({
          id: prospectDoc.id,
          ...prospectDoc.data(),
        }))
      );
    } catch (error) {
      console.error("❌ Failed to load Research Queue:", error);
      showStatus("error", "Failed to load Research Queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProspects();
  }, []);

  // ✅ Runs Research Manager
  // ✅ Purpose: Processes the highest-priority research queue items automatically
  // ✅ Safety: Does not generate drafts and does not contact prospects
  const handleRunResearchManager = async () => {
    try {
      setRunningResearchManager(true);

      showStatus(
        "info",
        "Research Manager is processing the Research Queue."
      );

      const functions = getFunctions();
      const runResearchManager = httpsCallable(functions, "runResearchManager");

      const result = await runResearchManager({
        researchLimit: 3,
      });

      console.log("✅ Research Manager result:", result.data);

      await loadProspects();

      showStatus(
        "success",
        `Research Manager complete. Completed: ${result.data.metrics?.completed || 0}. Failed: ${result.data.metrics?.failed || 0}.`
      );
    } catch (error) {
      console.error("❌ Research Manager failed:", error);

      showStatus(
        "error",
        "Research Manager failed. Check console for details."
      );
    } finally {
      setRunningResearchManager(false);
    }
  };
  
  const researchQueue = useMemo(() => {
    return prospects
      .filter((prospect) => {
        const hasResearchTarget = Boolean(prospect.website || prospect.sourceUrl);

        const needsResearch =
          prospect.collectionStage === "needs_research" ||
          !prospect.researchStatus ||
          prospect.researchStatus === "not_started" ||
          prospect.researchStatus === "failed";

        const notContacted =
          prospect.status !== "contacted" &&
          prospect.outreachStatus !== "sent";

        return hasResearchTarget && needsResearch && notContacted;
      })
      .sort((a, b) => {
        const priorityA = Number(a.queuePriority || a.fitScore || 0);
        const priorityB = Number(b.queuePriority || b.fitScore || 0);

        if (priorityB !== priorityA) return priorityB - priorityA;

        const createdA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const createdB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;

        return createdA - createdB;
      });
  }, [prospects]);

  const handleResearchProspect = async (prospect) => {
    try {
      setResearchingId(prospect.id);

      showStatus("info", `Researching ${prospect.eventName}.`);

      const functions = getFunctions();
      const researchProspect = httpsCallable(functions, "researchProspect");

      await researchProspect({
        prospectId: prospect.id,
      });

      await updateDoc(doc(db, "prospects", prospect.id), {
        collectionStage: "research_completed",
        updatedAt: serverTimestamp(),
      });

      await loadProspects();

      showStatus("success", `Research completed for ${prospect.eventName}.`);
    } catch (error) {
      console.error("❌ Research Agent failed:", error);
      showStatus("error", "Research Agent failed.");
    } finally {
      setResearchingId(null);
    }
  };

  const handleSkipResearch = async (prospect) => {
    const confirmed = window.confirm(
      `Skip research for "${prospect.eventName}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setWorkingId(prospect.id);

      await updateDoc(doc(db, "prospects", prospect.id), {
        collectionStage: "research_skipped",
        researchStatus: "skipped",
        queueReason: "Research skipped from Research Queue.",
        updatedAt: serverTimestamp(),
      });

      await loadProspects();

      showStatus("success", `${prospect.eventName} research skipped.`);
    } catch (error) {
      console.error("❌ Failed to skip research:", error);
      showStatus("error", "Failed to skip research.");
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <SalesPageLayout
      title="Research Queue"
      subtitle="Prospects ready for deeper website, contact, social, and sales research."
      status={status}
      actions={
        <>
          <button
            type="button"
            className="btn btn-info"
            onClick={handleRunResearchManager}
            disabled={runningResearchManager}
          >
            {runningResearchManager
              ? "Running Research Manager..."
              : "Run Research Manager"}
          </button>
          <Link className="btn btn-outline-secondary" to="/admin/sales-dashboard">
            Sales Dashboard
          </Link>

          <Link className="btn btn-outline-secondary" to="/admin/draft-queue">
            Draft Queue
          </Link>

          <Link className="btn btn-outline-secondary" to="/admin/prospects">
            Prospects CRM
          </Link>
        </>
      }
    >
      {loading ? (
        <div className="alert alert-info">Loading Research Queue...</div>
      ) : researchQueue.length === 0 ? (
        <div className="alert alert-info">No prospects currently need research.</div>
      ) : (
        <div className="row">
          {researchQueue.map((prospect) => (
            <div className="col-12 mb-3" key={prospect.id}>
              <div className="card">
                <div className="card-header d-flex justify-content-between">
                  <div>
                    <strong>{prospect.eventName}</strong>
                    {prospect.organizationName && (
                      <div className="small text-muted">
                        {prospect.organizationName}
                      </div>
                    )}
                  </div>

                  <span className="badge bg-info text-dark">
                    Score {prospect.fitScore || 0}/10
                  </span>
                </div>

                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <strong>Type:</strong> {prospect.eventType || "Unknown"}
                    </div>

                    <div className="col-md-3">
                      <strong>Location:</strong>{" "}
                      {prospect.city || prospect.state
                        ? `${prospect.city || ""}${prospect.city && prospect.state ? ", " : ""}${prospect.state || ""}`
                        : "Unknown"}
                    </div>

                    <div className="col-md-3">
                      <strong>Stage:</strong>{" "}
                      {prospect.collectionStage || "needs_research"}
                    </div>

                    <div className="col-md-3">
                      <strong>Research:</strong>{" "}
                      {prospect.researchStatus || "not started"}
                    </div>
                  </div>

                  {prospect.website && (
                    <div className="mb-2">
                      <strong>Website:</strong>{" "}
                      <a href={prospect.website} target="_blank" rel="noopener noreferrer">
                        {prospect.website}
                      </a>
                    </div>
                  )}

                  {prospect.sourceUrl && (
                    <div className="mb-2">
                      <strong>Source:</strong>{" "}
                      <a href={prospect.sourceUrl} target="_blank" rel="noopener noreferrer">
                        {prospect.sourceUrl}
                      </a>
                    </div>
                  )}

                  {prospect.aiSummary && (
                    <div className="small text-muted border rounded bg-light p-2 mb-3">
                      {prospect.aiSummary}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-info me-2"
                    onClick={() => handleResearchProspect(prospect)}
                    disabled={researchingId === prospect.id}
                  >
                    {researchingId === prospect.id
                      ? "Researching..."
                      : "Research Prospect"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary me-2"
                    onClick={() => handleSkipResearch(prospect)}
                    disabled={workingId === prospect.id}
                  >
                    Skip
                  </button>

                  <Link className="btn btn-outline-primary" to="/admin/prospects">
                    Edit in CRM
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </SalesPageLayout>
    );
}

export default ResearchQueue;